import { GenAiAssistantHistoryMessage } from '@/src/api/GenAiAPI';
import { useApi } from '@/src/store/api.context';
import { useUser } from '@/src/store/user.context';
import { ChatMessage } from '@/src/types/Chat';
import { FeatureName, useFeatureUsageTracker } from '@/src/utils/engagement/engagement-tracker';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Icon, Spinner } from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatButton from '../../ui/atoms/ChatButton/ChatButton';
import JamChat from '../../ui/molecules/MyJams/JamChat/JamChat';
import Linkify from '../../ui/atoms/Linkify/Linkify';
import { decodeHtmlCharCodes } from '@/src/utils/chat.utils';
import { Markdown } from '../Markdown';
import FeedbackButtons, { emptyFeedbackState, FeedbackState } from './FeedbackButtons';
import styles from './AIAssistantContainer.module.scss';

// Upper bound on history pages fetched when the panel opens (50 messages each).
const MAX_HISTORY_PAGES = 5;

const AIAssistantContainer: React.FC = () => {
  const { t } = useTranslation();
  const { genAiAPI } = useApi();
  const { user } = useUser();
  const trackFeatureUsage = useFeatureUsageTracker();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Feedback state keyed by queryId, owned here so it survives the chat window
  // being closed and reopened (FeedbackButtons unmounts when the panel closes).
  const [feedbackByQueryId, setFeedbackByQueryId] = useState<Record<string, FeedbackState>>({});
  // Conversation id from the service; undefined until the first answer or a loaded history.
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const historyRequested = useRef(false);
  // Set when a history load fails so the next open (not a re-render while open) retries it.
  const historyFailed = useRef(false);
  // False until the first history load settles, so the greeting and sample questions
  // don't flash on their own before the restored conversation is rendered.
  const [historySettled, setHistorySettled] = useState(false);

  const MIN_WIDTH = 320;
  const MIN_HEIGHT = 300;
  const DEFAULT_WIDTH = 400;
  const DEFAULT_HEIGHT = 600;

  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const isResizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing.current) return;
    const deltaX = startPos.current.x - e.clientX;
    const deltaY = startPos.current.y - e.clientY;
    setSize({
      width: Math.max(MIN_WIDTH, Math.min(startSize.current.width + deltaX, window.innerWidth - 80)),
      height: Math.max(MIN_HEIGHT, Math.min(startSize.current.height + deltaY, window.innerHeight - 150)),
    });
  }, []);

  const handleResizeEnd = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      startPos.current = { x: e.clientX, y: e.clientY };
      startSize.current = { ...size };
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    },
    [size, handleResizeMove, handleResizeEnd]
  );

  // Clamp chat size when the browser window shrinks
  useEffect(() => {
    const handleWindowResize = () => {
      setSize((prev) => ({
        width: Math.max(MIN_WIDTH, Math.min(prev.width, window.innerWidth - 80)),
        height: Math.max(MIN_HEIGHT, Math.min(prev.height, window.innerHeight - 150)),
      }));
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const createGreeting = useCallback(
    () =>
      ChatMessage.fromPlainObject({
        sender: t(i18nKeys.aiAssistant.senderName),
        message: t(i18nKeys.aiAssistant.greeting),
        timeSent: Date.now(),
        automated: true,
        senderParticipantId: 'ai-assistant',
      }),
    [t]
  );

  const toChatMessage = useCallback(
    (historyMessage: GenAiAssistantHistoryMessage) => {
      const isAssistant = historyMessage.role === 'assistant';
      const parsedTime = Date.parse(historyMessage.timestamp);
      return ChatMessage.fromPlainObject({
        sender: isAssistant ? t(i18nKeys.aiAssistant.senderName) : 'You',
        message: historyMessage.content,
        timeSent: Number.isNaN(parsedTime) ? Date.now() : parsedTime,
        automated: isAssistant,
        senderParticipantId: isAssistant ? 'ai-assistant' : user?.legacyLogin || 'user',
      });
    },
    [t, user?.legacyLogin]
  );

  // Add initial greeting message on mount
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([createGreeting()]);
    }
  }, [messages.length, createGreeting]);

  // Restore the most recent conversation the first time the panel is opened. A failure
  // leaves the panel on the greeting; the next question simply starts a new conversation.
  useEffect(() => {
    if (!isOpen) {
      // Re-arm a failed load on close so reopening the panel retries it, showing the loading
      // indicator again. Skip it once the user has started chatting: the retry couldn't
      // restore anything over their conversation, and the spinner would hide it.
      if (historyFailed.current && messages.length <= 1) {
        historyFailed.current = false;
        historyRequested.current = false;
        setHistorySettled(false);
      }
      return;
    }
    if (historyRequested.current) {
      return;
    }
    historyRequested.current = true;

    const loadHistory = async () => {
      try {
        const first = await genAiAPI.getAssistantHistory();
        if (!first.sessionId) {
          return;
        }
        const loaded = [...first.messages];
        let nextToken = first.nextToken;
        for (let page = 1; nextToken && page < MAX_HISTORY_PAGES; page++) {
          const next = await genAiAPI.getAssistantHistory(first.sessionId, nextToken);
          loaded.push(...next.messages);
          nextToken = next.nextToken;
        }
        if (loaded.length === 0) {
          return;
        }
        setSessionId((current) => current ?? first.sessionId ?? undefined);
        setMessages((current) => (current.length <= 1 ? [createGreeting(), ...loaded.map(toChatMessage)] : current));
      } catch {
        // History is best-effort; retry on the next open. A retry that succeeds after the user
        // has started chatting is ignored by the messages guard above.
        historyFailed.current = true;
      } finally {
        setHistorySettled(true);
      }
    };
    void loadHistory();
  }, [isOpen, messages.length, genAiAPI, createGreeting, toChatMessage]);

  const handleNewConversation = () => {
    setSessionId(undefined);
    setMessages([createGreeting()]);
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim() || isLoading) {
      return;
    }

    const userMessage = ChatMessage.fromPlainObject({
      sender: 'You',
      message,
      timeSent: Date.now(),
      automated: false,
      senderParticipantId: user?.legacyLogin || 'user',
    });

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Call the API
      const response = await genAiAPI.queryAssistant(message, sessionId);
      if (response.sessionId) {
        setSessionId(response.sessionId);
      }

      const aiMessage = ChatMessage.fromPlainObject({
        sender: t(i18nKeys.aiAssistant.senderName),
        message: response.answer,
        timeSent: Date.now(),
        automated: true,
        senderParticipantId: 'ai-assistant',
        queryId: response.queryId,
      });

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error: any) {
      const isRateLimit = error?.name === 'RateLimitError';
      const errorMessage = ChatMessage.fromPlainObject({
        sender: t(i18nKeys.aiAssistant.senderName),
        message: isRateLimit ? t(i18nKeys.aiAssistant.rateLimitMessage) : t(i18nKeys.aiAssistant.errorMessage),
        timeSent: Date.now(),
        automated: true,
        senderParticipantId: 'ai-assistant',
      });

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleChat = () => {
    // Track engagement only on the open transition (!isOpen -> open), not on close.
    if (!isOpen) {
      trackFeatureUsage(FeatureName.AI_ASSISTANT, 'click');
    }
    setIsOpen(!isOpen);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
  };

  const allSampleQuestions = [
    t(i18nKeys.aiAssistant.sampleQuestion1),
    t(i18nKeys.aiAssistant.sampleQuestion2),
    t(i18nKeys.aiAssistant.sampleQuestion3),
    t(i18nKeys.aiAssistant.sampleQuestion4),
  ];
  const askedMessages = new Set(messages.map((m) => m.message));
  const sampleQuestions = allSampleQuestions.filter((q) => !askedMessages.has(q));
  const showSampleQuestions = !isLoading && sampleQuestions.length > 0;
  const showNewChat = !isLoading && messages.length > 1;

  return (
    <div className={styles.container} data-testid="ai-assistant-container">
      {isOpen && (
        <div className={styles.chatOuter} style={{ width: size.width, height: size.height }}>
          <button
            type="button"
            className={styles.resizeHandle}
            onMouseDown={handleResizeStart}
            aria-label={t(i18nKeys.aiAssistant.resizeChatPanel)}
          />
          <div className={styles.chatHeader}>
            <div className={styles.chatHeaderTitle}>
              <img src="/assets/WavingHandMonkey.png" alt="" className={styles.chatHeaderIcon} />
              <Box variant="h3">{t(i18nKeys.aiAssistant.title)}</Box>
            </div>
            <div className={styles.chatHeaderRight}>
              <span className={styles.generatedByAI}>
                <Icon name="gen-ai" /> {t(i18nKeys.aiAssistant.generatedByAI)}
              </span>
              <button
                data-testid="minimize-ai-chat-button"
                aria-label={t(i18nKeys.jamChat.ariaLabels.minimizeChat)}
                type="button"
                className={styles.minimizeButton}
                onClick={handleToggleChat}
              >
                <Icon name="subtract-minus" />
              </button>
            </div>
          </div>
          <div className={styles.subHeader}>
            <Box variant="p" margin="xxs" color="text-status-info" textAlign={showNewChat ? 'left' : 'center'}>
              {t(i18nKeys.aiAssistant.disclaimer)}
            </Box>
            {/* Only offered once there's a conversation to clear and no answer is pending. */}
            {showNewChat && (
              <button
                data-testid="new-ai-conversation-button"
                title={t(i18nKeys.aiAssistant.newConversation)}
                type="button"
                className={styles.newChatButton}
                onClick={handleNewConversation}
              >
                <Icon name="refresh" size="small" /> {t(i18nKeys.aiAssistant.newChat)}
              </button>
            )}
          </div>
          <div className={`${styles.chat} ${isLoading ? styles.loading : ''}`}>
            {!historySettled ? (
              <div className={styles.loadingIndicator} data-testid="ai-assistant-history-loading">
                <Spinner size="normal" /> <span>{t(i18nKeys.aiAssistant.loadingHistory)}</span>
              </div>
            ) : (
              <JamChat
                disabled={false}
                ended={false}
                messages={messages}
                sendMessage={handleSendMessage}
                closeWindow={handleCloseChat}
                senderAvatar="/assets/monkey-in-circle.png"
                scrollToLastMessageTop
                renderMessageBody={(chatMessage) =>
                  chatMessage.automated ? (
                    <>
                      <Markdown content={chatMessage.message} className={styles.markdownMessage} />
                      {chatMessage.queryId && (
                        <FeedbackButtons
                          queryId={chatMessage.queryId}
                          feedback={feedbackByQueryId[chatMessage.queryId] ?? emptyFeedbackState}
                          onFeedbackChange={(next) =>
                            setFeedbackByQueryId((prev) => ({ ...prev, [chatMessage.queryId as string]: next }))
                          }
                        />
                      )}
                    </>
                  ) : (
                    <Linkify external>{decodeHtmlCharCodes(chatMessage.message)}</Linkify>
                  )
                }
                renderAfterMessages={
                  showSampleQuestions ? (
                    <div className={styles.sampleQuestions}>
                      {sampleQuestions.map((question) => (
                        <button
                          key={question}
                          type="button"
                          className={styles.sampleQuestionButton}
                          onClick={() => handleSendMessage(question)}
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  ) : undefined
                }
              />
            )}
            {isLoading && (
              <div className={styles.loadingIndicator}>
                <Spinner size="normal" /> <span>{t(i18nKeys.aiAssistant.thinking)}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.button}>
        <ChatButton
          onClick={handleToggleChat}
          chatType={undefined}
          title={t(i18nKeys.aiAssistant.buttonTitle)}
          counter={0}
          isActive={isOpen}
          id="aiAssistantButton"
        />
      </div>
    </div>
  );
};

export default AIAssistantContainer;
